'use client'
import { Comment, RootComment } from '@basehub/data/replicache/index'
import * as React from 'react'
import { createStore } from 'zustand'
import { useStoreWithEqualityFn } from 'zustand/traditional'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'

export type CommentsContextProps = {
  commentsMap: Map<string, Comment>
  roots: Set<string>
  rootsByBlockId: Map<string, Set<string>>
  rootsByDate: Map<string, string>
  rootsByRef: Map<NonNullable<RootComment['ref']>, Set<string>>
}

const createCommentsStore = (initProps?: Partial<CommentsContextProps>) => {
  const DEFAULT_PROPS: CommentsContextProps = {
    commentsMap: new Map(),
    roots: new Set(),
    rootsByBlockId: new Map(),
    rootsByDate: new Map(),
    rootsByRef: new Map(),
  }
  return createStore<CommentsContextProps>()(() => {
    return { ...DEFAULT_PROPS, ...initProps }
  })
}

type CommentsStore = ReturnType<typeof createCommentsStore>

export const CommentsContext = React.createContext<CommentsStore | null>(null)

export const CommentsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const store = React.useMemo(() => {
    return createCommentsStore()
  }, [])

  const { data } = useRepoReplicache()
  const replicache = data?.replicache

  React.useEffect(() => {
    if (!replicache) return

    const unsub = replicache.experimentalWatch(
      async (diff) => {
        const oldState = store.getState()
        const roots = new Set(oldState.roots)
        const rootsByBlockId = new Map(oldState.rootsByBlockId)
        const rootsByDate = new Map(oldState.rootsByDate)
        const rootsByRef = new Map(oldState.rootsByRef)
        const commentsMap = new Map(oldState.commentsMap)

        for (const diffOp of diff) {
          switch (diffOp.op) {
            case 'change':
            case 'add': {
              const comment = diffOp.newValue as Comment
              commentsMap.set(comment.id, comment)
              if (comment.type === 'root') {
                roots.add(comment.id)
                rootsByDate.set(comment.createdAt, comment.id)
                if (comment.blockId) {
                  const existing = rootsByBlockId.get(comment.blockId)
                  if (existing) {
                    rootsByBlockId.set(
                      comment.blockId,
                      existing.add(comment.id)
                    )
                  } else {
                    rootsByBlockId.set(comment.blockId, new Set([comment.id]))
                  }
                }
                if (comment.ref) {
                  const existing = rootsByRef.get(comment.ref)
                  if (existing) {
                    rootsByRef.set(comment.ref, existing.add(comment.id))
                  } else {
                    rootsByRef.set(comment.ref, new Set([comment.id]))
                  }
                }
              }
              break
            }
            case 'del': {
              const comment = diffOp.oldValue as Comment
              commentsMap.delete(comment.id)
              break
            }
            default:
              break
          }
        }

        store.setState({
          commentsMap,
          roots,
          rootsByBlockId,
          rootsByDate,
          rootsByRef,
        })
      },
      {
        prefix: 'comment',
        initialValuesInFirstDiff: true,
      }
    )

    return () => {
      unsub()
    }
  }, [replicache, store])

  return (
    <CommentsContext.Provider value={store}>
      {children}
    </CommentsContext.Provider>
  )
}

export const useCommentsStoreRoot = () => {
  const context = React.useContext(CommentsContext)
  if (!context) {
    throw new Error('useCommentsStore must be used within a CommentsProvider')
  }
  return context
}

export function useCommentsStore<T>(
  selector: (state: CommentsContextProps) => T,
  compare?: (prev: T, next: T) => boolean
): T {
  const store = useCommentsStoreRoot()
  return useStoreWithEqualityFn(store, selector, compare)
}

// -------
// helpers
// -------
export function getFullThread(
  commentsMap: Map<string, Comment>,
  commentId: string
): Comment[] | null {
  const startComment = commentsMap.get(commentId)
  if (!startComment) return null

  const thread: Comment[] = [startComment]
  let currentId = startComment.replyId

  while (currentId) {
    const comment = commentsMap.get(currentId)
    if (!comment) break
    thread.push(comment)
    currentId = comment.replyId
  }

  return thread
}

export function getCommentTail(
  commentsMap: Map<string, Comment>,
  commentId: string,
  callback?: (comment: Comment) => void
): Comment | null {
  let comment = commentsMap.get(commentId)
  if (!comment) return null

  while (comment.replyId) {
    const nextComment = commentsMap.get(comment.replyId)
    if (!nextComment) break
    if (callback) {
      callback(nextComment)
    }
    comment = nextComment
  }

  return comment
}
